<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  message: { type: String, default: '' },
  action: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const variant = computed(() => {
  return (
    props.action?.variant?.toLowerCase?.() ||
    props.action?.messageType?.toLowerCase?.() ||
    'info'
  );
});

const variantClasses = computed(() => {
  const variants = {
    success:
      'bg-n-green-3 text-n-green-12 border border-n-green-6/40 dark:bg-n-green-4/30 dark:border-n-green-7/40 dark:text-n-green-11',
    info: 'bg-n-slate-4 text-n-slate-12 border border-n-slate-6/40 dark:bg-n-slate-4 dark:border-n-slate-7/40 dark:text-n-slate-12',
    warning:
      'bg-n-amber-3 text-n-amber-12 border border-n-amber-6/40 dark:bg-n-amber-4/30 dark:border-n-amber-7/40 dark:text-n-amber-11',
    error:
      'bg-n-ruby-3 text-n-ruby-12 border border-n-ruby-6/40 dark:bg-n-ruby-4/30 dark:border-n-ruby-7/40 dark:text-n-ruby-11',
  };

  return variants[variant.value] || variants.info;
});

const iconName = computed(() => {
  const icons = {
    success: 'i-lucide-circle-check',
    info: 'i-lucide-info',
    warning: 'i-lucide-triangle-alert',
    error: 'i-lucide-circle-alert',
  };

  return icons[variant.value] || icons.info;
});

const iconClass = computed(() => {
  const classes = {
    success: 'text-n-green-10 dark:text-n-green-11',
    info: 'text-n-slate-11 dark:text-n-slate-12',
    warning: 'text-n-amber-10 dark:text-n-amber-11',
    error: 'text-n-ruby-10 dark:text-n-ruby-11',
  };

  return classes[variant.value] || classes.info;
});

const hasActionLink = computed(
  () => props.action?.type === 'link' && props.action?.message
);
</script>

<template>
  <div
    class="pointer-events-auto inline-flex max-w-full items-center gap-3 self-start rounded-xl px-4 py-3 shadow-lg backdrop-blur-sm"
    :class="variantClasses"
  >
    <div class="flex size-7 shrink-0 items-center justify-center rounded-full">
      <Icon :icon="iconName" class="size-[22px]" :class="iconClass" />
    </div>

    <div class="min-w-0">
      <p class="m-0 text-sm font-medium leading-5">
        {{ message }}
      </p>
      <router-link
        v-if="hasActionLink"
        :to="action.to"
        class="mt-1 inline-flex text-sm font-medium underline underline-offset-2"
      >
        {{ action.message }}
      </router-link>
    </div>

    <NextButton
      slate
      ghost
      sm
      class="shrink-0 self-center"
      @click="emit('close')"
    >
      <template #icon>
        <Icon icon="i-lucide-x" class="size-4 shrink-0" />
      </template>
    </NextButton>
  </div>
</template>
